/**
 * Долги: сколько они стоят и что гасить первым.
 *
 * Кредитка, ипотека, автокредит, рассрочка — один объект. Приложение делается
 * не под одного человека: у кого-то главная боль карта, у кого-то ипотека, а
 * у кого-то долгов нет вовсе. Поэтому порядок гашения **считается** по ставке
 * и остатку, а не назначается правилом «сначала карта».
 *
 * Ответ здесь даётся в деньгах, а не в процентах: «этот долг стоит 59 000 ₽ в
 * год» человек понимает сразу, а «39% годовых» требует умножения в уме.
 */
import { ZERO, add, clampPos, kop, roundHalfEven, type Money } from './money.js';
import type { Account, CreditTerms, Loan } from './types.js';

/**
 * Сколько долг стоит в год, если его не гасить.
 *
 * У карты в беспроцентном периоде это ноль, сколько бы ни была ставка: пока
 * долг по выписке закрыт, проценты не начисляются. Считать её по номинальной
 * ставке значило бы пугать человека, который всё делает правильно.
 */
export function annualCost(loan: Loan): Money {
  if (loan.principal <= ZERO || loan.ratePct <= 0) return ZERO;
  if (loan.kind === 'credit_card' && graceSecured(loan)) return ZERO;
  return kop(roundHalfEven((loan.principal * loan.ratePct) / 100));
}

/** Беспроцентный период сохранён: долг по выписке уже закрыт. */
export const graceSecured = (loan: Loan): boolean =>
  loan.card !== undefined && loan.card.reserved >= loan.card.statementDebt;

/**
 * Сколько сэкономит досрочный взнос за год.
 *
 * Считается как процент от внесённой суммы, а не по графику платежей: точная
 * экономия зависит от того, сокращается срок или платёж, и обещать здесь
 * точность до рубля было бы выдумкой. Порядку гашения этого достаточно —
 * он сравнивает долги между собой, а не предсказывает будущее.
 */
export function savedPerYear(loan: Loan, amount: Money): Money {
  if (!loan.allowsEarly || loan.ratePct <= 0 || amount <= ZERO) return ZERO;
  if (loan.kind === 'credit_card' && graceSecured(loan)) return ZERO;
  const capped = Math.min(amount, loan.principal);
  return kop(roundHalfEven((capped * loan.ratePct) / 100));
}

export type RepaymentMethod = 'rate' | 'size';

export interface RepaymentStep {
  loan: Loan;
  /** Во что обходится год этого долга. */
  cost: Money;
  /** Почему он стоит здесь — текст показывается человеку как есть. */
  reason: string;
}

/**
 * Очередь досрочного гашения.
 *
 * По ставке («лавина») экономит больше всего денег и потому стоит умолчанием.
 * По размеру («снежный ком») закрывает первый долг быстрее и даёт увидеть
 * результат — для того, кто раньше не выбирался из долгов, это иногда важнее
 * арифметики. Выбор остаётся за человеком, поэтому оба способа равноправны.
 *
 * Долги, которые нельзя гасить досрочно, и полностью закрытые в очередь не
 * попадают: предлагать невозможное — хуже, чем молчать.
 */
export function repaymentOrder(loans: readonly Loan[], method: RepaymentMethod = 'rate'): RepaymentStep[] {
  const open = loans.filter((l) => l.principal > ZERO && l.allowsEarly);
  const sorted = [...open].sort((a, b) => {
    if (method === 'size') return a.principal - b.principal || b.ratePct - a.ratePct;
    // Бесплатный долг уходит в конец: гасить его досрочно незачем.
    const ca = annualCost(a);
    const cb = annualCost(b);
    return b.ratePct - a.ratePct || cb - ca || a.principal - b.principal;
  });

  return sorted.map((loan, i) => {
    const cost = annualCost(loan);
    return { loan, cost, reason: reasonFor(loan, cost, i, method) };
  });
}

function reasonFor(loan: Loan, cost: Money, index: number, method: RepaymentMethod): string {
  if (cost === ZERO) {
    if (loan.ratePct <= 0) return 'Беспроцентный — досрочно гасить незачем';
    if (loan.kind === 'credit_card') return 'Беспроцентный период сохранён — процентов нет';
    return 'Процентов не начисляется';
  }
  if (index === 0) {
    return method === 'size'
      ? 'Самый маленький — закроется первым'
      // «Дороже» здесь — цена денег, а не рубли в год: крупный долг под низкую
      // ставку обходится в год дороже, но досрочный рубль в нём экономит меньше.
      // Прежняя подпись «дороже всех остальных» стояла рядом с меньшей суммой
      // в год и читалась как ошибка расчёта.
      : `Ставка ${fmtRate(loan.ratePct)} — самая высокая, гасить выгоднее всего`;
  }
  return method === 'size'
    ? `Следующий по размеру, ставка ${fmtRate(loan.ratePct)}`
    : `Ставка ${fmtRate(loan.ratePct)}`;
}

const fmtRate = (r: number): string =>
  `${(Math.round(r * 10) / 10).toString().replace('.', ',')}%`;

/** Сколько всего должен человек. */
export const totalDebt = (loans: readonly Loan[]): Money =>
  add(...loans.map((l) => (l.principal > ZERO ? l.principal : ZERO)));

/** Во что обходятся все долги за год. */
export const totalAnnualCost = (loans: readonly Loan[]): Money =>
  add(...loans.map(annualCost));

/**
 * Есть ли дорогой долг.
 *
 * Порог отделяет то, что имеет смысл гасить раньше накоплений, от ипотеки под
 * умеренный процент. Значение по умолчанию выбрано так, чтобы ипотека и
 * автокредит обычно оставались ниже, а карты и микрозаймы — выше.
 */
export const EXPENSIVE_RATE_PCT = 20;

export const hasExpensiveDebt = (loans: readonly Loan[], threshold = EXPENSIVE_RATE_PCT): boolean =>
  loans.some((l) => l.principal > ZERO && l.ratePct >= threshold && annualCost(l) > ZERO);

/**
 * Условия карт для расчёта «Можно потратить».
 *
 * Движок доступного работает с CreditTerms, и переписывать проверенную
 * денежную математику ради переименования полей было бы риском без выигрыша.
 * Поэтому карты отдаются ему в прежнем виде. Источник правды — запись
 * условий карты (см. `withCardFacts`); отсюда условия берутся, только когда
 * её нет.
 */
export function creditTermsFrom(loans: readonly Loan[]) {
  return loans
    .filter((l) => l.kind === 'credit_card' && l.card)
    .map((l) => ({
      accountId: l.card!.accountId,
      limit: l.card!.limit,
      statementDebt: l.card!.statementDebt,
      reserved: l.card!.reserved,
      // Необязательные условия переносятся только когда названы: при
      // `exactOptionalPropertyTypes` «есть поле со значением undefined» и
      // «поля нет» — разные вещи, и первое означало бы выдуманное условие.
      ...(l.card!.statementDay !== undefined ? { statementDay: l.card!.statementDay } : {}),
      paymentDay: l.paymentDay,
      graceDays: l.card!.graceDays,
      ...(l.card!.minPaymentPct !== undefined ? { minPaymentPct: l.card!.minPaymentPct } : {}),
      ...(l.card!.minPaymentFloor !== undefined ? { minPaymentFloor: l.card!.minPaymentFloor } : {}),
    }));
}

/**
 * Кредитка в списке долгов — по живым данным карты.
 *
 * Долг по карте меняют траты и переводы, а условия — лист карты, и всё это
 * пишется в счёт и в условия, а не в кредит. Кредит же хранит то, что было
 * названо в настройке. Пока главным считался он, перевод на карту не доходил
 * до расчёта: погашение записано, а в «Платежах» прежняя сумма.
 *
 * Остаток кредита — долг на счёте карты, условия — из записи карты. Ставка,
 * название и досрочное гашение остаются кредиту: их правит только он.
 */
export function withCardFacts(
  loans: readonly Loan[], accounts: readonly Account[], creditTerms: readonly CreditTerms[],
): Loan[] {
  return loans.map((l) => {
    if (l.kind !== 'credit_card' || !l.card) return l;
    const accountId = l.card.accountId;
    const account = accounts.find((a) => a.id === accountId);
    const t = creditTerms.find((x) => x.accountId === accountId);
    return {
      ...l,
      ...(account ? { principal: clampPos(kop(-account.balance)) } : {}),
      ...(t
        ? {
          paymentDay: t.paymentDay,
          card: {
            accountId,
            limit: t.limit,
            statementDebt: t.statementDebt,
            reserved: t.reserved,
            graceDays: t.graceDays,
            ...(t.statementDay !== undefined ? { statementDay: t.statementDay } : {}),
            ...(t.minPaymentPct !== undefined ? { minPaymentPct: t.minPaymentPct } : {}),
            ...(t.minPaymentFloor !== undefined ? { minPaymentFloor: t.minPaymentFloor } : {}),
          },
        }
        : {}),
    };
  });
}
